import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchCollection, fetchEntryBySlug } from '../lib/cms';
import Lightbox from '../components/Lightbox';

export default function GalleryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [galleries, setGalleries] = useState([]);
  const [currentGallery, setCurrentGallery] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Lightbox tilstand
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [allGalleries, page] = await Promise.all([
          fetchCollection('galleries'),
          fetchEntryBySlug('da', 'referencegalleri').catch(() => null)
        ]);
        setGalleries(allGalleries || []);
        if (page) {
          setPageData(page);
        } else {
          const altPage = await fetchEntryBySlug('da', 'galleri').catch(() => null);
          if (altPage) setPageData(altPage);
        }

        if (slug) {
          // Find det specifikke galleri baseret på slug
          const gallery = (allGalleries || []).find(g => (g.slug?.da || g.slug) === slug);
          if (gallery) {
            setCurrentGallery(gallery);
          } else {
            // Hvis galleriet ikke findes, gå til 404
            navigate('/404');
          }
        } else {
          setCurrentGallery(null);
        }
      } catch (err) {
        console.error('Error loading gallery data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [slug, navigate]);

  const handleOpenLightbox = (index) => {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  };

  const handlePrevImage = () => {
    if (!currentGallery?.images) return;
    setLightboxIndex((prev) => (prev === 0 ? currentGallery.images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    if (!currentGallery?.images) return;
    setLightboxIndex((prev) => (prev === currentGallery.images.length - 1 ? 0 : prev + 1));
  };

  const listTitle = pageData?.page_title?.da || pageData?.page_title || pageData?.title?.da || pageData?.title || "Referencegalleri";
  const listSubtitle = pageData?.page_subtitle?.da || pageData?.page_subtitle || "Gå på opdagelse i vores udførte projekter inden for nybyggeri, renovering, tagarbejde, vinduer, døre og murerarbejde.";

  // Metadata
  const metaTitle = currentGallery 
    ? `${currentGallery.title?.da || currentGallery.title} | Referencegalleri | SKT Byg` 
    : (pageData?.meta_title?.da || pageData?.meta_title || "Referencegalleri | Se vores udførte projekter | SKT Byg");
  const metaDesc = currentGallery 
    ? `Se billeder og referencer fra ${currentGallery.title?.da || currentGallery.title} udført af SKT Byg. Pålideligt tømrer- og murerarbejde i Galten og omegn.` 
    : (pageData?.meta_description?.da || pageData?.meta_description || "Gå på opdagelse i vores referencegalleri. Se billeder af nybyggeri, renovering, tagarbejde, vinduer, døre og murerarbejde udført af SKT Byg.");

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-light flex items-center justify-center">
        <div className="text-center space-y-4">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-accent"></i>
          <p className="text-brand-dark/60 font-semibold">Henter galleridata...</p>
        </div>
      </div>
    );
  }

  // DETALJE VISNING (Et specifikt galleri)
  if (currentGallery) {
    return (
      <div className="min-h-screen bg-brand-light py-16">
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDesc} />
          <meta property="og:title" content={metaTitle} />
          <meta property="og:description" content={metaDesc} />
          {currentGallery.images?.[0] && <meta property="og:image" content={currentGallery.images[0]} />}
        </Helmet>

        <div className="mx-auto max-w-7xl px-6">
          {/* Brødkrumme & Tilbage-knap */}
          <div className="mb-8">
            <Link
              to="/referencegalleri"
              className="inline-flex items-center gap-2 text-sm font-bold text-brand-accent hover:text-brand-accent-hover transition-colors duration-300"
            >
              <i className="fa-solid fa-arrow-left"></i>
              <span>Tilbage til alle gallerier</span>
            </Link>
          </div>

          {/* Galleri Info */}
          <div className="mb-12 space-y-4">
            <h1 className="text-3xl md:text-4xl font-extrabold text-brand-dark">
              {currentGallery.title.da}
            </h1>
            {currentGallery.description?.da && (
              <div 
                className="text-brand-dark/70 leading-relaxed max-w-3xl prose prose-sm"
                dangerouslySetInnerHTML={{ __html: currentGallery.description.da }}
              />
            )}
          </div>

          {/* Billedgrid */}
          {currentGallery.images && currentGallery.images.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {currentGallery.images.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => handleOpenLightbox(idx)}
                  className="relative h-64 rounded-2xl overflow-hidden border border-brand-sand shadow-sm cursor-pointer group"
                >
                  <img
                    src={img}
                    alt={`${currentGallery.title.da} - Billede ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-all duration-300">
                    <div className="h-12 w-12 rounded-full bg-brand-accent text-white flex items-center justify-center opacity-0 group-hover:opacity-100 scale-90 group-hover:scale-100 transition-all duration-300 shadow-md">
                      <i className="fa-solid fa-magnifying-glass-plus text-lg"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border border-brand-sand">
              <p className="text-brand-dark/50 font-semibold">Der er i øjeblikket ingen billeder i dette galleri.</p>
            </div>
          )}
        </div>

        {/* Lightbox */}
        {isLightboxOpen && currentGallery.images && (
          <Lightbox
            images={currentGallery.images}
            currentIndex={lightboxIndex}
            onClose={() => setIsLightboxOpen(false)}
            onPrev={handlePrevImage}
            onNext={handleNextImage}
          />
        )}
      </div>
    );
  }

  // LISTE VISNING (Alle gallerier)
  return (
    <div className="min-h-screen bg-brand-light py-16">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/1595576254044627-0-700.jpg" />
      </Helmet>

      <div className="mx-auto max-w-7xl px-6">
        {/* Sidetitel */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl md:text-5xl font-extrabold text-brand-dark mb-4">
            {listTitle}
          </h1>
          <p className="text-lg text-brand-dark/70 leading-relaxed">
            {listSubtitle}
          </p>
        </div>

        {/* Gallerigrid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {galleries.map((gallery) => (
            <Link
              key={gallery.id}
              to={`/referencegalleri/${gallery.slug?.da || gallery.slug}`}
              className="group bg-white rounded-2xl border border-brand-sand overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col cursor-pointer"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={gallery.images?.[0] || "https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/no-image.png"}
                  alt={gallery.title?.da || gallery.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-300"></div>
              </div>
              <div className="p-6 flex-grow flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-bold text-brand-dark group-hover:text-brand-accent transition-colors duration-300 mb-2">
                    {gallery.title?.da || gallery.title}
                  </h3>
                  {(gallery.description?.da || gallery.description) && (
                    <div 
                      className="text-sm text-brand-dark/60 line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: gallery.description?.da || gallery.description }}
                    />
                  )}
                </div>
                <span className="text-xs font-bold text-brand-accent flex items-center gap-1.5 mt-4">
                  Se billeder <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform duration-300"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* Erhverv & Privatkunder lister fra scrape */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-white p-8 md:p-12 rounded-3xl border border-brand-sand shadow-sm">
          {/* Erhvervskunder */}
          <div className="space-y-6">
            <h3 className="text-xl font-extrabold text-brand-dark flex items-center gap-3">
              <i className="fa-solid fa-building text-brand-accent"></i>
              <span>Erhvervskunder</span>
            </h3>
            <p className="text-sm font-bold text-brand-dark/80">Et udpluk af vores erhvervskunder:</p>
            <ul className="space-y-2.5 text-sm text-brand-dark/70">
              <li>– <a href="http://www.scanex.eu/" target="_blank" rel="noopener noreferrer" className="hover:text-brand-accent underline">Scanex, Skovby</a></li>
              <li>– <a href="http://www.storring-stjaer.dk/index.php?id=379" target="_blank" rel="noopener noreferrer" className="hover:text-brand-accent underline">Storring-Stjær Menighedsråd</a></li>
              <li>– <a href="http://www.tendens-bolig.dk/" target="_blank" rel="noopener noreferrer" className="hover:text-brand-accent underline">Tendens Bolig, Egå</a></li>
              <li>– Aarhus Kommune, Teknisk Forvaltning</li>
              <li>– Pig City, Stjær</li>
              <li>– <a href="http://www.sengespecialisten.dk/" target="_blank" rel="noopener noreferrer" className="hover:text-brand-accent underline">Sengespecialisten, Aarhus, Kolding, Randers, Aalborg, Silkeborg</a></li>
            </ul>
          </div>

          {/* Privatkunder */}
          <div className="space-y-6 md:border-l md:border-brand-sand md:pl-12">
            <h3 className="text-xl font-extrabold text-brand-dark flex items-center gap-3">
              <i className="fa-solid fa-house-user text-brand-accent"></i>
              <span>Privatkunder</span>
            </h3>
            <p className="text-sm font-bold text-brand-dark/80">Et udpluk af vores privatkunder:</p>
            <ul className="space-y-2.5 text-sm text-brand-dark/70">
              <li>– Charlotte Støckler, Skanderborg</li>
              <li>– Michael Kristensen, Stjær</li>
              <li>– Kaare Egelund Schmidt, Skovby</li>
              <li>– Erik og Birgitte Snorgaard, Storring</li>
              <li>– Kirsten og Anders Key Nielsen, Højbjerg</li>
              <li>– Carsten Marvig, Storring</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
